import * as React from "react";
import {
	AsyncSelect,
	type Option as AsyncSelectOption,
} from "@/components/forms/AsyncSelect";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { UseFormReturn } from "react-hook-form";
import type {
	MaterialOption,
	PurchaseFormValues,
	SupplierOption,
} from "../types";

export type PurchasesFormProps = {
	dialogTitle?: string;
	open: boolean;
	form: UseFormReturn<PurchaseFormValues>;
	isSubmitting: boolean;
	supplierOption: SupplierOption | null;
	onSupplierQuery: (value: string) => Promise<AsyncSelectOption[]>;
	onRequestNewSupplier: () => void;
	materialOption: MaterialOption | null;
	onMaterialQuery: (value: string) => Promise<AsyncSelectOption[]>;
	onRequestNewMaterial: () => void;
	disableMaterial: boolean;
	onSubmit: React.FormEventHandler<HTMLFormElement>;
	onCancel: () => void;
	onClose: () => void;
};

export const PurchasesForm: React.FC<PurchasesFormProps> = ({
	dialogTitle = "Nova Compra",
	open,
	form,
	isSubmitting,
	supplierOption,
	onSupplierQuery,
	onRequestNewSupplier,
	materialOption,
	onMaterialQuery,
	onRequestNewMaterial,
	disableMaterial,
	onSubmit,
	onCancel,
	onClose,
}) => {
	const supplierInitialOptions = React.useMemo(
		() =>
			supplierOption
				? [
						{
							value: supplierOption.id,
							label: supplierOption.name,
						},
					]
				: [],
		[supplierOption],
	);

	const materialInitialOptions = React.useMemo(
		() =>
			materialOption
				? [
						{
							value: materialOption.id,
							label: materialOption.label,
						},
					]
				: [],
		[materialOption],
	);

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => (!nextOpen ? onClose() : null)}
		>
			<DialogContent className="max-h-[90dvh] w-[95vw] max-w-3xl overflow-y-auto p-0">
				<DialogHeader className="px-6 pb-2 pt-6">
					<DialogTitle>{dialogTitle}</DialogTitle>
					<DialogDescription>
						Preencha os detalhes da compra. Pode criar fornecedores e materiais
						a partir deste formulário.
					</DialogDescription>
				</DialogHeader>

				<Form {...form}>
					<form className="space-y-6 px-6 pb-6" onSubmit={onSubmit}>
						<div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
							<FormField
								control={form.control}
								name="supplier"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Fornecedor</FormLabel>
										<FormControl>
											<AsyncSelect
												value={field.value || undefined}
												placeholder="Fornecedor"
												onQueryChange={onSupplierQuery}
												initialOptions={supplierInitialOptions}
												onChange={(value) => field.onChange(value ?? "")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex items-end">
								<Button type="button" onClick={onRequestNewSupplier}>
									Novo
								</Button>
							</div>
						</div>

						<div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
							<FormField
								control={form.control}
								name="material"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Material</FormLabel>
										<FormControl>
											<AsyncSelect
												value={field.value || undefined}
												placeholder="Material"
												onQueryChange={onMaterialQuery}
												initialOptions={materialInitialOptions}
												onChange={(value) => field.onChange(value ?? "")}
												disabled={disableMaterial}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex items-end">
								<Button
									type="button"
									size="sm"
									onClick={onRequestNewMaterial}
									disabled={disableMaterial}
								>
									Novo
								</Button>
							</div>
						</div>

						<div className="grid gap-4 sm:grid-cols-2">
							<FormField
								control={form.control}
								name="quantity"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Quantidade</FormLabel>
										<FormControl>
											<Input
												type="number"
												min={1}
												{...field}
												onChange={(event) => field.onChange(event.target.value)}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="price"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Preço Unitário</FormLabel>
										<FormControl>
											<div className="flex items-stretch overflow-hidden rounded-md border bg-background">
												<Input
													type="number"
													min={0}
													step="0.01"
													className="border-none"
													{...field}
													onChange={(event) =>
														field.onChange(event.target.value)
													}
												/>
												<span className="flex items-center bg-muted px-3 text-sm">
													€
												</span>
											</div>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<FormField
							control={form.control}
							name="origin"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Origem</FormLabel>
									<FormControl>
										<select
											{...field}
											className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										>
											<option value="">Selecione a origem</option>
											<option value="europe">Europa</option>
											<option value="other">Fora da Europa</option>
										</select>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="link"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Link da Compra</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Link da Compra" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="observation"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Observações</FormLabel>
									<FormControl>
										<textarea
											{...field}
											rows={3}
											placeholder="Observações"
											className="min-h-[96px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
							<Button
								type="button"
								variant="outline"
								onClick={onCancel}
								disabled={isSubmitting}
							>
								Cancelar
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? "A guardar…" : "Guardar"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};
